/**
 * finance-core — Nghĩa vụ nợ tháng này (Đ3, quyết định 17/07/2026)
 *
 * Trả lời câu hỏi số 2 của người mang nợ: "Tháng này cần trả tổng cộng
 * bao nhiêu, hạn gần nhất khi nào?" — tính thuần từ dữ liệu, dùng chung
 * cho trang Tài sản và trang chủ để hai nơi luôn ra CÙNG một con số.
 *
 * Luật chống đếm trùng (phản biện P3): với mỗi khoản nợ/thẻ, nếu đã có
 * plan Dự kiến Thanh toán đang chờ trong tháng → dùng số của plan (con số
 * người dùng chủ động đặt); chỉ khi không có plan mới dùng PMT/tối thiểu
 * ước tính, đánh dấu isEstimate.
 */

import { cardLinkedLoanBalance, cardOwnBalance } from './balance';

export interface ObligationDebt {
  id: string;
  lender?: string;
  status?: string;
  remainingBalance?: number;
  repaymentMethod?: string;
  monthlyPayment?: number | null;
  /** ISO string hoặc Date — ngày đến hạn (nếu có) */
  dueDate?: string | Date | null;
  type?: string;
  creditCardId?: string | null;
}

export interface ObligationCard {
  id: string;
  name?: string;
  type?: string;
  usedCredit?: number;
  minimumPaymentPercent?: number | string;
  paymentDueDay?: number | string;
  status?: string;
}

export interface ObligationPlan {
  paymentType?: string;
  debtId?: string;
  creditCardId?: string;
  amount?: number;
  status?: string;
  year?: number;
  month?: number;
}

export interface ObligationCardPayment {
  creditCardId?: string;
  amount?: number;
  /** Firestore Timestamp (có toDate) hoặc Date/ISO string */
  date?: { toDate?: () => Date } | Date | string | null;
}

export interface ObligationItem {
  key: string;
  label: string;
  amount: number;
  /** true khi là ước tính (PMT/tối thiểu), false khi lấy từ plan người dùng đặt */
  isEstimate: boolean;
  source: 'plan' | 'annuity' | 'card-minimum';
  /** ngày đến hạn trong tháng nếu xác định được */
  dueDate: Date | null;
}

export interface MonthlyObligations {
  total: number;
  items: ObligationItem[];
  /** hạn gần nhất còn ở phía trước (hoặc gần nhất trong tháng nếu đã qua hết) */
  nearestDue: Date | null;
  hasEstimates: boolean;
}

const DEFAULT_MIN_PAYMENT_PERCENT = 5;

/** Ngày đến hạn kế tiếp của thẻ từ paymentDueDay (ngày trong tháng). */
export const nextCardDueDate = (paymentDueDay: number, from: Date = new Date()): Date | null => {
  const day = Math.min(Math.max(Math.round(paymentDueDay), 1), 31);
  if (!day) return null;
  const y = from.getFullYear();
  const m = from.getMonth();
  const clamp = (yy: number, mm: number, dd: number) => {
    const lastDay = new Date(yy, mm + 1, 0).getDate();
    return new Date(yy, mm, Math.min(dd, lastDay));
  };
  const thisMonth = clamp(y, m, day);
  return thisMonth >= new Date(y, m, from.getDate()) ? thisMonth : clamp(y, m + 1, day);
};

const toDate = (v: ObligationCardPayment['date']): Date | null => {
  if (!v) return null;
  const d = typeof v === 'object' && 'toDate' in v && v.toDate ? v.toDate() : new Date(v as string | Date);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Mẫu định kỳ trả thẻ/nợ đang bật: thẻ/khoản vay có mẫu thì khoản của từng THÁNG do mẫu sinh ra (và có tháng bắt đầu), không ước tính thêm. */
export interface ObligationTemplate { type?: string; active?: boolean; creditCardId?: string; debtId?: string; walletId?: string }

/** Hạn trả của thẻ TRONG THÁNG đang xét (kể cả khi ngày đó đã qua) — khác `nextCardDueDate` có thể nhảy sang tháng sau. */
export const cardDueThisMonth = (paymentDueDay: number, now: Date): Date | null => {
  const day = Math.round(paymentDueDay);
  if (!(day >= 1)) return null;
  return new Date(now.getFullYear(), now.getMonth(), Math.min(day, new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()));
};

export const computeMonthlyObligations = (
  debts: ObligationDebt[],
  sources: ObligationCard[],
  plannedPayments: ObligationPlan[],
  creditPayments: ObligationCardPayment[] = [],
  now: Date = new Date(),
  templates: ObligationTemplate[] = []
): MonthlyObligations => {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  // Plan đang chờ trong tháng, gom theo khoản nợ/thẻ (một khoản có thể nhiều plan)
  const pendingPlans = plannedPayments.filter(
    p => p.status === 'pending' && p.year === year && p.month === month
  );
  const planByDebt = new Map<string, number>();
  const planByCard = new Map<string, number>();
  for (const p of pendingPlans) {
    if (p.paymentType === 'debt' && p.debtId) {
      planByDebt.set(p.debtId, (planByDebt.get(p.debtId) || 0) + (Number(p.amount) || 0));
    } else if (p.paymentType === 'credit' && p.creditCardId) {
      planByCard.set(p.creditCardId, (planByCard.get(p.creditCardId) || 0) + (Number(p.amount) || 0));
    }
  }

  // ĐÃ TRẢ trong tháng — nghĩa vụ được trừ đi phần đã hoàn thành, để
  // "đã thanh toán nhưng vẫn bị đề xuất" không xảy ra:
  // - Plan đã CONFIRMED tháng này = tiền đã đi (cả nợ lẫn thẻ)
  // - creditPayments tháng này = trả thẻ trực tiếp không qua plan
  const paidByDebt = new Map<string, number>();
  const paidByCard = new Map<string, number>();
  for (const p of plannedPayments) {
    if (p.status !== 'confirmed' || p.year !== year || p.month !== month) continue;
    if (p.paymentType === 'debt' && p.debtId) {
      paidByDebt.set(p.debtId, (paidByDebt.get(p.debtId) || 0) + (Number(p.amount) || 0));
    }
    // Thẻ: plan confirmed sinh bản ghi creditPayments — KHÔNG cộng ở đây
    // để tránh đếm trùng với creditPayments bên dưới
  }
  for (const cp of creditPayments) {
    const d = toDate(cp.date);
    if (!d || d.getFullYear() !== year || d.getMonth() + 1 !== month) continue;
    if (cp.creditCardId) {
      paidByCard.set(cp.creditCardId, (paidByCard.get(cp.creditCardId) || 0) + (Number(cp.amount) || 0));
    }
  }

  const governedCards = new Set<string>(), governedDebts = new Set<string>();
  for (const t of templates) {
    if (t.active === false) continue;
    // Thẻ đang được dùng theo một mẫu CHI hằng tháng (quẹt thẻ định kỳ): người dùng quản lý thẻ đó qua mẫu chi, không nhắc ước tính tối thiểu
    if (t.type === 'expense' && t.walletId) governedCards.add(t.walletId);
    if (t.type !== 'payment') continue;
    if (t.creditCardId) governedCards.add(t.creditCardId);
    if (t.debtId) governedDebts.add(t.debtId);
  }

  const items: ObligationItem[] = [];

  // Khoản vay: plan (ưu tiên) → PMT trả góp đều → bỏ qua (trả tự do không có nghĩa vụ cố định)
  for (const d of debts) {
    if (d.status !== 'active' || (Number(d.remainingBalance) || 0) <= 0) continue;
    if (d.type === 'credit') continue; // sổ di sản — thẻ tính bên dưới

    const due = d.dueDate ? new Date(d.dueDate) : null;
    const dueValid = due && !Number.isNaN(due.getTime()) ? due : null;

    const paidThisMonth = paidByDebt.get(d.id) || 0;
    const planned = planByDebt.get(d.id);
    if (planned && planned > 0) {
      items.push({
        key: `debt:${d.id}`, label: d.lender || 'Khoản vay', amount: planned,
        isEstimate: false, source: 'plan', dueDate: dueValid
      });
    } else if (!governedDebts.has(d.id) && d.repaymentMethod === 'annuity' && (Number(d.monthlyPayment) || 0) > 0) {
      // Kỳ cuối: không trả quá dư nợ + lãi tháng
      const pmt = Math.min(
        Number(d.monthlyPayment),
        (Number(d.remainingBalance) || 0) * 1.05 // trần an toàn ~ dư nợ + lãi
      );
      // Trừ phần đã trả tháng này (plan đã xác nhận) — trả rồi thì hết nghĩa vụ
      const remaining = Math.max(0, pmt - paidThisMonth);
      if (remaining > 0) {
        items.push({
          key: `debt:${d.id}`, label: d.lender || 'Khoản vay', amount: remaining,
          isEstimate: true, source: 'annuity', dueDate: dueValid
        });
      }
    }
  }

  // Thẻ tín dụng: plan (ưu tiên) → tối thiểu ước tính = % × dư nợ
  // (phần vay/trả góp gắn thẻ đã có nghĩa vụ riêng ở khoản vay, không tính tối thiểu hai lần)
  const linkedLoans = cardLinkedLoanBalance(debts as any[]);
  for (const s of sources) {
    if (s.type !== 'credit') continue;
    const used = Number(s.usedCredit) || 0;
    if (used <= 0) continue;
    const ownUsed = cardOwnBalance(used, linkedLoans.get(s.id) ?? 0);

    const dueDay = Number(s.paymentDueDay) || 0;
    const due = dueDay > 0 ? cardDueThisMonth(dueDay, now) : null;

    const paidThisMonth = paidByCard.get(s.id) || 0;
    const planned = planByCard.get(s.id);
    if (planned && planned > 0) {
      items.push({
        key: `card:${s.id}`, label: s.name || 'Thẻ tín dụng', amount: planned,
        isEstimate: false, source: 'plan', dueDate: due
      });
    } else if (!governedCards.has(s.id)) {
      const pct = Number(s.minimumPaymentPercent) || DEFAULT_MIN_PAYMENT_PERCENT;
      // Trừ phần đã trả tháng này — đã trả đủ tối thiểu thì không đề xuất nữa
      const remaining = Math.max(0, Math.round(ownUsed * pct / 100) - paidThisMonth);
      if (remaining > 0) {
        items.push({
          key: `card:${s.id}`, label: s.name || 'Thẻ tín dụng',
          amount: remaining,
          isEstimate: true, source: 'card-minimum', dueDate: due
        });
      }
    }
  }

  const total = items.reduce((sum, i) => sum + i.amount, 0);
  const futureDues = items
    .map(i => i.dueDate)
    .filter((d): d is Date => d !== null && d >= new Date(now.getFullYear(), now.getMonth(), now.getDate()))
    .sort((a, b) => a.getTime() - b.getTime());

  return {
    total,
    items: items.sort((a, b) => b.amount - a.amount),
    nearestDue: futureDues[0] || null,
    hasEstimates: items.some(i => i.isEstimate)
  };
};

export interface YearlyOutlookItem { id: string; name: string; amount: number; next: Date; daysLeft: number; perMonth: number }
/** Khoản chi/trả hằng năm (bảo hiểm, phí thường niên…) từ mẫu định kỳ: ngày đến hạn kế tiếp và số cần để dành mỗi tháng. */
export const yearlyOutlook = (
  templates: Array<{ id: string; name?: string; type?: string; frequency?: string; active?: boolean; amount?: number; dayOfMonth?: number; monthOfYear?: number }>,
  now: Date = new Date()
): YearlyOutlookItem[] => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const out: YearlyOutlookItem[] = [];
  for (const t of templates) {
    if (t.frequency !== 'yearly' || t.active === false || t.type === 'income') continue;
    const amount = Number(t.amount) || 0;
    if (amount <= 0) continue;
    const month = Math.min(12, Math.max(1, Math.round(Number(t.monthOfYear) || 1)));
    const day = Math.min(31, Math.max(1, Math.round(Number(t.dayOfMonth) || 1)));
    const at = (y: number) => new Date(y, month - 1, Math.min(day, new Date(y, month, 0).getDate()));
    const next = at(today.getFullYear()) >= today ? at(today.getFullYear()) : at(today.getFullYear() + 1);
    out.push({ id: t.id, name: t.name || 'Khoản hằng năm', amount, next, daysLeft: Math.round((next.getTime() - today.getTime()) / 86_400_000), perMonth: Math.ceil(amount / 12) });
  }
  return out.sort((a, b) => a.next.getTime() - b.next.getTime());
};
